import { Injectable } from '@angular/core';
import { DocumentSelection } from '@models/documents/document-selection';
import { ReminderSettingTypeEnum } from '@models/reminders/reminder.enum';
import { AnnualStatementSettingsData } from "@models/settings/annual-statement-settings-data";
import { AsicAgentData } from '@models/settings/asic-agent-data';
import { DocumentSettingsData } from '@models/settings/document-settings-data';
import { IEmailSettingsData } from "@models/settings/email-settings-data";
import { OrganisationSettingData } from '@models/settings/organisation-setting-data';
import { ReminderSettingsData } from '@models/settings/reminder-settings-data';
import BaseApiService from "@shared/services/base/base-api.service";
import { Operation } from 'fast-json-patch';
import { map, Observable } from 'rxjs';
import { filterPatchOperations } from '../utils/filter-patch-operations';

@Injectable({ providedIn: 'root' })
export class SettingsApiService extends BaseApiService {
  getOrganisationSettings(): Observable<OrganisationSettingData> {
    return this.api.get<OrganisationSettingData>(`${ this.apiUrl }/settings`)
      .pipe(
        map(settings => new OrganisationSettingData(settings ?? {}))
      );
  }

  patchOrganisationSettings(operations: Operation[]): Observable<OrganisationSettingData> {
    return this.api.patch<OrganisationSettingData>(`${ this.apiUrl }/settings`, filterPatchOperations(operations), { headers: { 'Content-Type': 'application/json-patch+json' } });
  }

  getAgentSettings(): Observable<AsicAgentData> {
    return this.api.get<AsicAgentData>(`${ this.apiUrl }/settings/asic`)
      .pipe(
        map(agent => new AsicAgentData(agent ?? {}))
      );
  }

  patchAgentChanges(operations: Operation[]): Observable<AsicAgentData> {
    return this.api.patch<AsicAgentData>(`${ this.apiUrl }/settings/asic`, filterPatchOperations(operations), { headers: { 'Content-Type': 'application/json-patch+json' } });
  }

  getDocumentSettings(): Observable<DocumentSettingsData> {
    return this.api.get<DocumentSettingsData>(`${ this.apiUrl }/settings/document`)
      .pipe(
        map(settings => new DocumentSettingsData(settings ?? {}))
      );
  }

  patchDocumentSettings(operations: Operation[]): Observable<DocumentSettingsData> {
    return this.api.patch<DocumentSettingsData>(`${ this.apiUrl }/settings/document`, filterPatchOperations(operations), { headers: { 'Content-Type': 'application/json-patch+json' } });
  }

  getAnnualStatementSettings(): Observable<AnnualStatementSettingsData> {
    return this.api.get<AnnualStatementSettingsData>(`${ this.apiUrl }/settings/annual-statement`)
      .pipe(
        map(settings => new AnnualStatementSettingsData(settings ?? {}))
      );
  }

  getAnnualStatementSelection(): Observable<DocumentSelection[]> {
    return this.api.get<DocumentSelection[]>(`${ this.apiUrl }/settings/annual-statement/selection`);
  }

  patchAnnualStatementSettings(operations: Operation[]): Observable<AnnualStatementSettingsData> {
    return this.api.patch<AnnualStatementSettingsData>(`${ this.apiUrl }/settings/annual-statement`, filterPatchOperations(operations), { headers: { 'Content-Type': 'application/json-patch+json' } });
  }

  getReminderSettings(): Observable<ReminderSettingsData> {
    return this.api.get<ReminderSettingsData>(`${ this.apiUrl }/settings/reminder`)
      .pipe(
        map(settings => new ReminderSettingsData(settings ?? {}))
      );
  }

  patchReminderSettings(type: ReminderSettingTypeEnum, operations: Operation[]): Observable<ReminderSettingsData> {
    return this.api.patch<ReminderSettingsData>(`${ this.apiUrl }/settings/reminder/${ type }`, filterPatchOperations(operations), { headers: { 'Content-Type': 'application/json-patch+json' } });
  }

  loadEmailSettings(): Observable<IEmailSettingsData> {
    return this.api.get<IEmailSettingsData>(`${ this.apiUrl }/settings/email`);
  }
}
